import App from './App.vue';
import { createApp } from 'vue';
import * as Sentry from '@sentry/vue';

// routes
import router from './router';

// store
import { createPinia } from 'pinia';
import { storeInitializationPlugin } from './store/plugins/storeInitializationPlugin';

// mdi library
import '@mdi/font/css/materialdesignicons.css';

// vuetify
import 'vuetify/dist/vuetify.css';
import { createVuetify } from 'vuetify';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';

import { capitalize, abbreviateNumber } from '@/utils/GlobalMethods'

import { interact } from './directives/Interact';
import logServiceInstance from './api/services/LogService';

import VueGtag from 'vue-gtag';
import { analyticsConfig } from "@/config/analyticsConfig";

import { primaryBorder } from '@/directives/PrimaryBorder';
import { primaryGlow } from '@/directives/PrimaryGlow';

import { initializePollfishConfig } from './config/pollfishConfig';

// app initialization
const app = createApp(App);

const pinia = createPinia();
// Register the store initialization plugin
pinia.use(storeInitializationPlugin);
logServiceInstance.setup();

app.directive('primary-border', primaryBorder);
app.directive('primary-glow', primaryGlow);
app.directive('interact', interact);

Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENV,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    // Adjust sampling rates based on environment
    tracesSampleRate: import.meta.env.VITE_ENV === 'production' ? 0.2 : 1.0,
    replaysSessionSampleRate: import.meta.env.VITE_ENV === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    release: import.meta.env.VITE_VERSION || 'development',
    beforeSend(event) {
      event.tags = {
        ...event.tags,
        environment: import.meta.env.VITE_ENV,
        isDevelopment: import.meta.env.VITE_ENV !== 'production'
      };
      return event;
    }
  });

app.use(pinia);
app.use(router);

function initializeGoogleAnalytics() {
    app.use(VueGtag, {
        config: { id: analyticsConfig.PROD },
    });
}

// Check consent status
if (localStorage.getItem('cookieConsent') === 'true') {
    initializeGoogleAnalytics();
}

app.config.globalProperties.$capitalize = capitalize
app.config.globalProperties.$abbreviateNumber = abbreviateNumber;

// Listen for the custom event triggered when the user grants consent
document.addEventListener('user-consent-granted', initializeGoogleAnalytics);

app.config.globalProperties.$capitalize = capitalize;

const vuetifyOptions = {
    components,
    directives,
    defaults: {
        VTextField: {
            bgColor: "formBackground",
            borderRadius: "8px",
        },
    },
    theme: {
        defaultTheme: 'dark',
        customProperties: true,
        themes: {
            dark: {
                colors: {
                    primary: '#F45484',
                    secondary: '#424242',
                    primaryBackground: '#1E1E1E',
                    secondaryBackground: '#1F2129',
                    formBackground: '#16171D',
                    arrowButton: '#2D2F3B',
                    greyish: '#252831',
                    tileBackground: '#21242C',
                    roundChipBackground: '#373A49',
                    error: '#FF5252',
                    info: '#2196F3',
                    success: '#4CAF50',
                    warning: '#FFC107',
                    action: '#3E68B3',
                }
            },
        },
    }
};

const vuetify = createVuetify(vuetifyOptions);
app.use(vuetify);

initializePollfishConfig();

app.mount('#app');
